import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { userLogin, changePassword } from "../../Action/api.js"; // Import userLogin API
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { CheckCircle } from "@mui/icons-material";

export const Login = () => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [openForgotPassword, setOpenForgotPassword] = useState(false);
  const [passwordChangeData, setPasswordChangeData] = useState({
    userId: "",
    dateOfBirth: null,
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordResetStatus, setPasswordResetStatus] = useState({
    loading: false,
    success: false,
    error: "",
  });
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const navigate = useNavigate();

  // Role-to-Route Mapping
  const roleRoutes = {
    "AUJ-GROUP-001": "/founder-dashboard", // Chief Product Officer
    "U-PT-ROLE-001": "/cpo-dashboard", // Chief Product Officer
    "U-PT-ROLE-002": "/product-manager-dashboard", // Product Manager
    "U-PT-ROLE-003": "/associate-product-manager-dashboard", // Associate Product Manager
    "U-PT-ROLE-004": "/junior-product-manager-dashboard", // Junior Product Manager
    "U-PT-ROLE-005": "/product-intern-dashboard", // Product Intern / Trainee
    "U-MG-ROLE-001": "/cmo-dashboard", // Chief Marketing Officer
    "U-MG-ROLE-002": "/growth-marketing-dashboard", // Growth Marketing Manager
    "U-MG-ROLE-003": "/content-strategist-dashboard", // Content Strategist
    "U-MG-ROLE-004": "/social-media-dashboard", // Social Media Executive
    "U-SB-ROLE-001": "/vp-sales-dashboard", // VP of Sales
    "U-SB-ROLE-002": "/sales-manager-dashboard", // Sales Manager
    "U-SB-ROLE-003": "/business-development-dashboard", // Business Development Executive
    "U-SB-ROLE-004": "/executive-trainee-dashboard", // Executive Trainee
    "U-HR-ROLE-001": "/executive-hr-dashboard", // Executive Trainee
    "U-HR-ROLE-002": "/senior-trainee-dashboard", // Executive Trainee
    "U-HR-ROLE-003": "/mid-senior-trainee-dashboard", // Executive Trainee
    "U-HR-ROLE-004": "/junior-trainee-dashboard", // Executive Trainee
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    setErrorMessage("");

    const formData = new FormData(event.currentTarget);
    const username = formData.get("username");
    const password = formData.get("user_password");

    try {
      // Query Firestore to check if the username exists      // Call the login API
      const requestPayload = {
        emailId: username,
        password: password,
      };
      const response = await userLogin(requestPayload);

      if (!response.data || response.isAxiosError || !response.data.status) {
        setErrorMessage(
          response.data?.uiMsg || "Login failed. Please check your credentials."
        );
        setLoading(false);
        return;
      }

      const responseData = response.data;
      const userData = responseData.userData;

      if (userData) {
        // Extract user data from API response userData object
        const uId = userData.uId || ""; // This is also moved to uId
        const userName = userData.name || ""; // This moved to staffFullName 
        const roleId = userData.roleId || "";
        const roleName = userData.roleName || "";
        const depId = userData.depId || "";
        const depName = userData.depName || "";
        const gradeCode = userData.grade || "";

        // Store authorization token from response
        localStorage.setItem("authToken", responseData.authorizationToken || "");
        localStorage.setItem("isAuthenticated", "true"); // Store login status
        localStorage.setItem("loginTime", Date.now());

        localStorage.setItem("userId", username); // Store user ID
        localStorage.setItem("userName", userName); // Name
        localStorage.setItem("staffId", uId); // Store user ID
        localStorage.setItem("roleId", roleId); // Store role ID
        localStorage.setItem("roleName", roleName); // Store user role
        localStorage.setItem("depId", depId); // Dep ID
        localStorage.setItem("depName", depName); // Store department name
        localStorage.setItem("gradeCode", gradeCode); // Store grade
        localStorage.setItem("userData", JSON.stringify(userData)); // Store userData

        // Redirect based on roleId
        const route = roleRoutes[roleId];
        if (route) {
          navigate("/task-dashboard"); // Redirect to the corresponding dashboard
        } else {
          navigate("/task-dashboard"); // Fallback route if roleId not found
        }
      } else {
        setErrorMessage("Invalid username or password!");
      }
    } catch (error) {
      console.error("Login error:", error);
      setErrorMessage("An error occurred during login. Please try again.");
    }

    setLoading(false);
  };

  const handleForgotPasswordOpen = () => {
    setOpenForgotPassword(true);
    setPasswordChangeData({
      userId: "",
      dateOfBirth: null,
      newPassword: "",
      confirmPassword: "",
    });
    setPasswordResetStatus({
      loading: false,
      success: false,
      error: "",
    });
  };

  const handleForgotPasswordClose = () => {
    setOpenForgotPassword(false);
  };

  const handlePasswordChangeInput = (e) => {
    const { name, value } = e.target;
    setPasswordChangeData({
      ...passwordChangeData,
      [name]: value,
    });
  };

  const handleDateChange = (date) => {
    setPasswordChangeData({
      ...passwordChangeData,
      dateOfBirth: date,
    });
  };

  const formatDateForApi = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;

  };

  const handlePasswordReset = async () => {
    // Validate inputs
    if (!passwordChangeData.userId) {
      setPasswordResetStatus({
        loading: false,
        success: false,
        error: "Please enter your User ID",
      });
      return;
    }

    if (!passwordChangeData.dateOfBirth) {
      setPasswordResetStatus({
        loading: false,
        success: false,
        error: "Please select your Date of Birth",
      });
      return;
    }

    if (!passwordChangeData.newPassword) {
      setPasswordResetStatus({
        loading: false,
        success: false,
        error: "Please enter a new password",
      });
      return;
    }

    if (passwordChangeData.newPassword !== passwordChangeData.confirmPassword) {
      setPasswordResetStatus({
        loading: false,
        success: false,
        error: "Passwords do not match",
      });
      return;
    }

    // Start loading
    setPasswordResetStatus({
      loading: true,
      success: false,
      error: "",
    });

    try {
      // Format date as required by API
      const formattedDate = formatDateForApi(passwordChangeData.dateOfBirth);

      // Call the API
      const response = await changePassword({
        userId: passwordChangeData.userId,
        dateOfBirth: formattedDate,
        newPassword: passwordChangeData.newPassword,
      });

      if (response.status) {
        // Success
        setPasswordResetStatus({
          loading: false,
          success: true,
          error: "",
        });

        // Close the dialog after 3 seconds
        setTimeout(() => {
          handleForgotPasswordClose();
        }, 3000);
      } else {
        // Error from API
        setPasswordResetStatus({
          loading: false,
          success: false,
          error:
            response.uiMsg ||
            "Failed to reset password. Please try again.",
        });
      }
    } catch (error) {
      console.error("Password reset error:", error);
      setPasswordResetStatus({
        loading: false,
        success: false,
        error: "An unexpected error occurred. Please try again.",
      });
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "80vh",
        backgroundColor: "#f5f5f5",
        padding: 2,
        overflow: "hidden", // Prevent scrollbars
      }}
    >
      <Paper
        elevation={3}
        sx={{
          padding: 4,
          maxWidth: "400px",
          width: "100%",
          borderRadius: 2,
          textAlign: "center",
          maxHeight: "90vh", // Ensure it fits within the viewport
          overflowY: "auto", // Add scroll inside the box if content overflows
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom>
          Welcome Back
        </Typography>
        <Typography variant="body1" color="textSecondary" gutterBottom>
          Please log in to your account
        </Typography>

        {errorMessage && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <form onSubmit={handleLogin} autoComplete="off">
          <TextField
            label="Username"
            name="username"
            id="username"
            variant="outlined"
            fullWidth
            margin="normal"
            required
            autoComplete="off" // Disable autocomplete for this field
          />
          {/* <input
            type="password"
            style={{ display: "none" }}
            autoComplete="new-password"
          /> */}

          <TextField
            label="Password"
            name="user_password"
            id="user_password"
            type={showPassword ? "text" : "password"}
            variant="outlined"
            fullWidth
            margin="normal"
            required
            autoComplete="new-password" // Prevent browser from suggesting saved passwords
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{
              marginTop: 2,
              padding: 1.5,
              fontWeight: "bold",
              fontSize: "16px",
              backgroundColor: "#228B22", // Forest Green color
              "&:hover": {
                backgroundColor: "#1e7a1e", // Slightly darker green on hover
              },
            }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : "Login"}
          </Button>
        </form>

        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Button
            color="primary"
            onClick={handleForgotPasswordOpen}
            sx={{
              textTransform: "none",
              fontSize: "14px",
              "&:hover": {
                background: "transparent",
                textDecoration: "underline",
              },
            }}
          >
            Forgot Password?
          </Button>
        </Box>

        {/* Forgot Password Dialog */}
        <Dialog
          open={openForgotPassword}
          onClose={handleForgotPasswordClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            sx={{
              borderBottom: "1px solid #e0e0e0",
              backgroundColor: "#f8f9fa",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" component="div">
              Reset Password
            </Typography>
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleForgotPasswordClose}
              aria-label="close"
            >
              <VisibilityOff />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ pt: 3, pb: 1 }}>
            {passwordResetStatus.success ? (
              <Box sx={{ textAlign: "center", py: 2 }}>
                <Typography
                  variant="h6"
                  color="success.main"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 2,
                  }}
                >
                  <CheckCircle sx={{ mr: 1 }} />
                  Password Changed Successfully!
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  You can now log in with your new password.
                </Typography>
              </Box>
            ) : (
              <>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Please enter your user ID and date of birth to reset your
                  password.
                </Typography>

                {passwordResetStatus.error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {passwordResetStatus.error}
                  </Alert>
                )}

                <TextField
                  label="User ID"
                  name="userId"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={passwordChangeData.userId}
                  onChange={handlePasswordChangeInput}
                  disabled={passwordResetStatus.loading}
                  required
                  autoComplete="off"
                />

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Date of Birth"
                    value={passwordChangeData.dateOfBirth}
                    onChange={handleDateChange}
                    disabled={passwordResetStatus.loading}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        margin: "normal",
                        required: true,
                        variant: "outlined",
                      },
                    }}
                  />
                </LocalizationProvider>

                <TextField
                  label="New Password"
                  name="newPassword"
                  type={passwordVisible ? "text" : "password"}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={passwordChangeData.newPassword}
                  onChange={handlePasswordChangeInput}
                  disabled={passwordResetStatus.loading}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setPasswordVisible(!passwordVisible)}
                          edge="end"
                        >
                          {passwordVisible ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Confirm Password"
                  name="confirmPassword"
                  type={confirmPasswordVisible ? "text" : "password"}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={passwordChangeData.confirmPassword}
                  onChange={handlePasswordChangeInput}
                  disabled={passwordResetStatus.loading}
                  required
                  error={
                    passwordChangeData.newPassword !==
                      passwordChangeData.confirmPassword &&
                    passwordChangeData.confirmPassword !== ""
                  }
                  helperText={
                    passwordChangeData.newPassword !==
                      passwordChangeData.confirmPassword &&
                    passwordChangeData.confirmPassword !== ""
                      ? "Passwords do not match"
                      : ""
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setConfirmPasswordVisible(!confirmPasswordVisible)
                          }
                          edge="end"
                        >
                          {confirmPasswordVisible ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 2, pt: 1, borderTop: "1px solid #e0e0e0" }}>
            <Button
              onClick={handleForgotPasswordClose}
              color="inherit"
              disabled={passwordResetStatus.loading}
            >
              Cancel
            </Button>

            {!passwordResetStatus.success && (
              <Button
                onClick={handlePasswordReset}
                variant="contained"
                color="primary"
                disabled={
                  passwordResetStatus.loading ||
                  !passwordChangeData.userId ||
                  !passwordChangeData.dateOfBirth ||
                  !passwordChangeData.newPassword ||
                  passwordChangeData.newPassword !== passwordChangeData.confirmPassword
                }
                sx={{
                  backgroundColor: "#228B22",
                  "&:hover": {
                    backgroundColor: "#1e7a1e",
                  },
                }}
              >
                {passwordResetStatus.loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Reset Password"
                )}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
};

export default Login;
